<!-- 
The navigation drawer for the system.

This appears down the left hand side of the screen and changes in size and presentation
depending on the screen size.
-->
<template>
  <v-navigation-drawer
    :value="value || !xs"
    :clipped="$vuetify.breakpoint.smAndUp"
    :mini-variant="mini"
    :mobile-break-point="$vuetify.breakpoint.thresholds.xs"
    fixed app 
    class="main-nav-drawer accent"
    @input="$emit('input', $event)"
  >
    <v-list dense>
      <v-list-tile to="/">
        <v-list-tile-action>
          <v-tooltip 
            v-if="mini"
            right open-delay="0" close-delay="0" transition="null"
          >
            <v-icon slot="activator" large>home</v-icon>
            Home
          </v-tooltip>
          <v-icon v-else>home</v-icon>
        </v-list-tile-action>
        <v-list-tile-title v-if="!mini">Home</v-list-tile-title>
      </v-list-tile>

      <v-list-group 
        v-for="(menu, index) in menus" 
        :key="`menu-${index}`" 
        :value="isOpen(menu)" 
        @click="open(menu)"
      >
        <template v-slot:activator>
          <v-list-tile >
            <v-list-tile-title >{{ menu.text }}</v-list-tile-title>
          </v-list-tile>
        </template>
        <span v-if="mini" slot="prependIcon">
          <v-tooltip right open-delay="0" close-delay="0" transition="null">
            <v-icon slot="activator" large class="mt-3 mb-2">{{ menu.icon }}</v-icon>
            {{ menu.text }}
          </v-tooltip>
        </span>
        <v-icon v-else slot="prependIcon" class="mt-3 mb-2">{{ menu.icon }}</v-icon>
        <v-list-tile 
          v-for="(child, childIndex) in menu.children" 
          :key="`menu-${index}-${childIndex}`" 
          @click="open(child)"
        >
          <v-list-tile-action v-if="mini" style="justify-content: center;">
            <v-tooltip right open-delay="0" close-delay="0" transition="null">
              <v-icon slot="activator" :class="{'primary--text': isActive(child)}">{{ child.icon }}</v-icon>
              {{ child.text }}
            </v-tooltip>
          </v-list-tile-action>
          <v-list-tile-title :class="isActive(child) ? 'primary--text' : ''" class="indent">
            {{ child.text }}
          </v-list-tile-title>
          <v-list-tile-action v-if="!mini">
            <v-icon :class="isActive(child) ? 'primary--text' : ''">{{ child.icon }}</v-icon>
          </v-list-tile-action>
        </v-list-tile>
      </v-list-group>

      <v-list-tile :href="userGuideLink" target="_blank">
        <v-list-tile-action>
          <v-tooltip 
            v-if="mini" 
            right open-delay="0" close-delay="0" transition="null"
          >
            <v-icon slot="activator" large>help</v-icon>
            User guide
          </v-tooltip>
          <v-icon v-else>help</v-icon>
        </v-list-tile-action>
        <v-list-tile-title v-if="!mini">User guide</v-list-tile-title>
      </v-list-tile>

    </v-list>
  </v-navigation-drawer>
</template>

<script>
import * as claimTypes from "@/store/claim-types";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [lookupMixins],
  props: {
    value: {
      required: true,
      validator: prop => typeof prop === "boolean" || prop === null
    }
  },
  computed: {
    menus() {
      const result = [];

      if (this.levyCollection && this.levyCollection.id) {
        const levyCollectionId = this.levyCollection.id;
        const getUrl = suffix => `/levy-collections/${levyCollectionId}/${suffix}`;
        const type = this.lookups.levyCollectionTypeDescriptions[this.levyCollection.type];
        const isSandbox = this.doesLookupHaveAttribute(
          this.levyCollection.type,
          "IsSandbox",
          this.lookups.levyCollectionTypes
        );
        const description = `${this.levyCollection.year} ${type}${!isSandbox ? " levy collection" : ""}`;
        const menu = {
          text: description,
          icon: "how_to_vote",
          url: getUrl(isSandbox ? "sandbox" : "dashboard")
        };
        if (isSandbox) {
          menu.children = [
            { text: "Sandbox", icon: "move_to_inbox", levyCollectionId, url: getUrl("sandbox"), exact: true },
            { text: "Providers", icon: "school", levyCollectionId, url: getUrl("providers"), exact: true },
            { text: "Overrides", icon: "change_history", levyCollectionId, url: getUrl("overrides"), exact: true },
            { text: "History", icon: "history", levyCollectionId, url: getUrl("history"), exact: true }
          ];
        } else {
          menu.children = [
            { text: "Dashboard", icon: "dashboard", levyCollectionId, url: getUrl("dashboard"), exact: true },
            { text: "Workflow", icon: "playlist_add_check", levyCollectionId, url: getUrl("workflow"), exact: true },
            { text: "Providers", icon: "school", levyCollectionId, url: getUrl("providers"), exact: true },
            { text: "Overrides", icon: "change_history", levyCollectionId, url: getUrl("overrides"), exact: true },
            { text: "Notices", icon: "email", levyCollectionId, url: getUrl("notices"), exact: true },
            { text: "Invoices", icon: "attach_money", levyCollectionId, url: getUrl("invoices"), exact: false },
            { text: "Setup", icon: "settings", levyCollectionId, url: getUrl("setup") },
            { text: "History", icon: "history", levyCollectionId, url: getUrl("history"), exact: true }
          ];
        }
        result.push(menu);
      }

      const listings = {
        text: "Complete listings",
        icon: "assignment",
        children: [
          {
            text: "Levy collections",
            icon: "how_to_vote",
            url: "/levy-collections",
            exact: true
          },
          { text: "Providers", icon: "school", url: "/providers" },
          { text: "Notices", icon: "email", url: "/notices" },
          { text: "Invoices", icon: "attach_money", url: "/invoices" }
        ]
      };
      result.push(listings);

      const admin = { text: "Administration", icon: "network_check", children: [] };
      if (this.claims.includes(claimTypes.SYSTEM_LOGS_VIEW)) {
        admin.children.push({ text: "System logs", icon: "history", url: "/system-logs" });
      }
      if (this.claims.includes(claimTypes.EXCEPTION_LOGS_VIEW)) {
        admin.children.push({ text: "Exception logs", icon: "warning", url: "/exception-logs" });
      }
      if (this.claims.includes(claimTypes.SCHEDULED_JOBS_VIEW)) {
        admin.children.push({ text: "Scheduled jobs", icon: "av_timer", url: "/scheduled-jobs" });
      }
      if (this.claims.includes(claimTypes.ROLES_VIEW)) {
        admin.children.push({ text: "Roles & permissions", icon: "lock_open", url: "/roles" });
      }
      if (this.claims.includes(claimTypes.USERS_VIEW)) {
        admin.children.push({ text: "Users", icon: "people", url: "/users" });
      }
      if (this.claims.includes(claimTypes.AUTHENTICATION_LOGS_VIEW)) {
        admin.children.push({ text: "Authentication Logs", icon: "security", url: "/authentication-logs" });
      }
      if (admin.children.length > 0) {
        result.push(admin);
      }

      return result;
    },
    state() {
      return this.$store.state.levyCollections.view;
    },
    mini() {
      return !this.value && !this.xs;
    },
    xs() {
      return this.$vuetify.breakpoint.xs;
    },
    loaded() {
      return this.state.loaded;
    },
    levyCollection() {
      return this.state.data;
    },
    claims() {
      return this.$store.getters.claims;
    },
    isSignedIn() {
      return this.$store.getters.isSignedIn;
    },
    userGuideLink() {
      if(this.$store.state.system.details.environment === "local"){
        //localhost uses a webpack dev server to serve userguide
        //for higher environments, userguide is published inside subfolder of the root api app
        return `${process.env.VUE_APP_LOCALHOST_USER_GUIDE}`;
      }
      return `${process.env.VUE_APP_USER_GUIDE_BASE_URL}`;
    }
  },
  methods: {
    open(menu) {
      if (menu.url) {
        if (menu.children && this.isOpen(menu)) {
          return;
        }
        this.$router.push(menu.url);
      }
    },
    isActive(childMenu) {
      const isLevyCollection = childMenu.levyCollectionId ? true : false;

      const onLevyCollectionProvider =
        this.$route.name != null && this.$route.name.startsWith("levy-collection-provider");
      if (onLevyCollectionProvider) {
        return childMenu.text == "Providers" && isLevyCollection;
      }

      const onNoticeTemplate = this.$route.name != null && this.$route.name.startsWith("notice-template");
      if (onNoticeTemplate) {
        return childMenu.text == "Setup" && isLevyCollection;
      }

      const onInvoice = this.$route.name == "invoice";
      const arrivedFrom = this.$store.state.invoices.view.arrivedFrom;
      const arrivedFromLevyCollection = arrivedFrom != null && arrivedFrom.includes("/levy-collections");
      if (onInvoice && arrivedFromLevyCollection) {
        return childMenu.text == "Invoices" && isLevyCollection;
      }

      return this.isSameUrl(childMenu);
    },
    isOpen(parentMenu) {
      if (this.$route.path == parentMenu.url) {
        return true;
      } else if (parentMenu.children) {
        return parentMenu.children.some(this.isActive);
      } else {
        return false;
      }
    },
    isSameUrl(menu) {
      const path = this.$route.path;
      return (menu.exact == true && path == menu.url) || (menu.exact != true && path.startsWith(menu.url));
    }
  }
};
</script>

<style lang="scss">
.v-list__tile__title {
  &.indent {
    margin-left: 40px !important;
  }
}
</style>